import React, { useState, useEffect, useCallback } from "react";
import LandingPage from "./LandingPage";
import CasinoFigures from "./CasinoFigures";
import BlogList from "./components/BlogList";
import BlogPost from "./components/BlogPost";
import { AuthProvider, useAuth } from "./context/AuthContext";
import AuthModal from "./components/AuthModal";

function parsePath(pathname) {
  if (pathname.startsWith("/blog/")) {
    const slug = pathname.replace("/blog/", "").replace(/\/$/, "");
    if (slug) {
      return { view: "blog-post", slug };
    }
    return { view: "blog", slug: null };
  }
  if (pathname === "/blog" || pathname === "/blog/") {
    return { view: "blog", slug: null };
  }
  if (pathname === "/dashboard" || pathname === "/dashboard/") {
    return { view: "dashboard", slug: null };
  }
  if (pathname === "/demo" || pathname === "/demo/") {
    return { view: "demo", slug: null };
  }
  if (pathname === "/terms" || pathname === "/terms/") {
    return { view: "landing", slug: null, legalTab: "terms" };
  }
  if (pathname === "/privacy" || pathname === "/privacy/") {
    return { view: "landing", slug: null, legalTab: "privacy" };
  }
  if (pathname === "/responsible-gaming" || pathname === "/responsible-gaming/") {
    return { view: "landing", slug: null, legalTab: "responsible" };
  }
  if (pathname === "/disclaimer" || pathname === "/disclaimer/") {
    return { view: "landing", slug: null, legalTab: "disclaimer" };
  }
  return { view: "landing", slug: null };
}

function MainApp() {
  const [route, setRoute] = useState(() => parsePath(window.location.pathname));
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const { user, loading } = useAuth();

  // Listen to browser Back / Forward buttons
  useEffect(() => {
    const handlePopState = () => {
      setRoute(parsePath(window.location.pathname));
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigateTo = useCallback((view, path = "/", slug = null) => {
    if (window.location.pathname !== path) {
      window.history.pushState(null, "", path);
    }
    setRoute({ view, slug });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const showLanding = () => navigateTo("landing", "/");
  const showBlog = () => navigateTo("blog", "/blog");
  const showBlogPost = (slug) => navigateTo("blog-post", `/blog/${slug}`, slug);
  const showDemo = () => navigateTo("demo", "/demo");

  const showDashboard = () => {
    if (!user) {
      setAuthModalOpen(true);
      return;
    }
    navigateTo("dashboard", "/dashboard");
  };

  // If user signs out while on dashboard, return to landing
  useEffect(() => {
    if (!loading && !user && route.view === "dashboard") {
      navigateTo("landing", "/");
    }
  }, [user, loading, route.view, navigateTo]);

  return (
    <div className="App">
      {route.view === "dashboard" && user ? (
        <CasinoFigures onBackToLanding={showLanding} />
      ) : route.view === "demo" ? (
        <CasinoFigures
          isDemo={true}
          onBackToLanding={showLanding}
          onSignUp={() => setAuthModalOpen(true)}
        />
      ) : route.view === "blog" ? (
        <BlogList
          onSelectPost={showBlogPost}
          onBackToHome={showLanding}
          onViewDashboard={showDashboard}
        />
      ) : route.view === "blog-post" && route.slug ? (
        <BlogPost
          slug={route.slug}
          onBackToBlog={showBlog}
          onViewDashboard={showDashboard}
        />
      ) : (
        <LandingPage
          onViewDashboard={showDashboard}
          onPreviewData={showDemo}
          onViewBlog={showBlog}
          onSelectBlogPost={showBlogPost}
          initialLegalTab={route.legalTab}
        />
      )}

      {/* Global Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode="signin"
        onSuccess={() => {
          setAuthModalOpen(false);
          navigateTo("dashboard", "/dashboard");
        }}
      />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}

export default App;
